/**
 * Home's widgets (LED-1151), as pure derivations from data the console
 * already loads — the inventory rows (`/api/items`), the recent walks
 * (`/api/walks`) and the project cards (`/api/projects`).
 *
 * Nothing here fetches and nothing here renders. Every number a widget prints
 * is computed in this file, so a test can pin each one without a DOM.
 *
 * ## What is derived, and what is not available yet
 *
 * - STREAK. There is no streak history on the server. It is derived from
 *   walk start times (all projects) or daily-log record creation times (one
 *   project), counted in WORKING days — a Saturday off does not break a
 *   superintendent's streak, which is why the design's strip reads
 *   WED THU FRI MON TUE.
 * - PUNCH / OBSERVATIONS / TASKS. Counted from the inventory rows. Who is
 *   "holding" an item is the record's RESOLVED assignee company only — the
 *   free-text holder field is meaningless on real data and is never read
 *   (see `tests/guards/noBallAffordance.test.ts`). Rows with nobody resolved
 *   on them count toward totals and never form a bucket of their own.
 */

import type { ConsoleRow, ConsoleWalkResponse, ProjectCardRow, ConsoleSite } from "../../api";

const DAY_MS = 86_400_000;
const DOW = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"] as const;

/**
 * An observation, however it is stored. A Procore-format project keeps an
 * observation as a daily-log row (`kind: "log"`, `recordType: "observation"`)
 * — the same rule the nav's count and the list use (`matchesSavedView`), so
 * the widget and the nav can never disagree about what an observation is.
 */
export function isObservation(row: ConsoleRow): boolean {
  return row.kind === "observation" || row.recordType === "observation";
}

/** A row that is still work: not closed, and not a daily log (a record, not work). */
export function isOpen(row: ConsoleRow): boolean {
  if (row.reviewStatus === "rejected" || row.status === "closed") return false;
  // An accepted observation with no status of its own projects as "logged"
  // (a daily-log row's default), but it is open until someone closes it.
  if (isObservation(row)) return true;
  return row.status !== "logged";
}

function startOfDay(at: Date): Date {
  const day = new Date(at);
  day.setHours(0, 0, 0, 0);
  return day;
}

function dayKey(at: Date): string {
  return `${at.getFullYear()}-${at.getMonth()}-${at.getDate()}`;
}

function isWorkday(at: Date): boolean {
  const dow = at.getDay();
  return dow !== 0 && dow !== 6;
}

/** The working day before `at`. */
function previousWorkday(at: Date): Date {
  const day = startOfDay(at);
  do day.setDate(day.getDate() - 1);
  while (!isWorkday(day));
  return day;
}

/** Whole days between two instants' calendar days (local). */
export function calendarDaysBetween(earlier: Date, later: Date): number {
  return Math.round((startOfDay(later).getTime() - startOfDay(earlier).getTime()) / DAY_MS);
}

/** "today" / "yesterday" / "Mon" / "Oct 20" — a short, human relative day. */
export function relativeDay(at: Date, now: Date): string {
  const days = calendarDaysBetween(at, now);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][at.getDay()]!;
  return at.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function clock(at: Date): string {
  const hours = at.getHours();
  const twelve = hours % 12 || 12;
  return `${twelve}:${String(at.getMinutes()).padStart(2, "0")}${hours < 12 ? "a" : "p"}`;
}

// ---------------------------------------------------------------------------
// Streak
// ---------------------------------------------------------------------------

export interface StreakDay {
  label: string;
  done: boolean;
  /** The working day that is today and has nothing yet — the amber ring. */
  today: boolean;
}

export interface Streak {
  /** Consecutive working days with activity, ending today or yesterday. */
  days: number;
  doneToday: boolean;
  last?: Date;
  /** The five most recent working days, oldest first, ending today. */
  strip: StreakDay[];
}

export function streakOf(instants: Date[], now: Date): Streak {
  const active = new Set(instants.filter((at) => !Number.isNaN(at.getTime())).map(dayKey));
  const today = startOfDay(now);
  const doneToday = active.has(dayKey(today));

  // Count back from today if it is done, from the last working day if not.
  // A weekend day with a walk counts toward nothing and breaks nothing.
  let cursor = doneToday || !isWorkday(today) ? today : previousWorkday(today);
  if (!isWorkday(cursor)) cursor = previousWorkday(cursor);
  let days = 0;
  while (active.has(dayKey(cursor))) {
    days += 1;
    cursor = previousWorkday(cursor);
  }

  const strip: StreakDay[] = [];
  let day = isWorkday(today) ? today : previousWorkday(today);
  for (let i = 0; i < 5; i++) {
    const done = active.has(dayKey(day));
    strip.unshift({
      label: DOW[day.getDay()]!,
      done,
      today: !done && dayKey(day) === dayKey(today),
    });
    day = previousWorkday(day);
  }

  const last = instants
    .filter((at) => !Number.isNaN(at.getTime()) && at.getTime() <= now.getTime())
    .sort((a, b) => b.getTime() - a.getTime())[0];
  return { days, doneToday, last, strip };
}

export interface StreakWidget extends Streak {
  kind: string;
  title: string;
  sub: string;
}

/** The walk streak (all projects) or the daily-log streak (one project). */
export function streakWidget(input: {
  scoped: boolean;
  walks: ConsoleWalkResponse[];
  rows: ConsoleRow[];
  siteName: (id: string) => string;
  now: Date;
}): StreakWidget {
  const { scoped, walks, rows, now } = input;
  const instants = scoped
    ? rows.filter((row) => row.kind === "log").map((row) => new Date(row.createdAt))
    : walks.map((walk) => new Date(walk.startedAt));
  const streak = streakOf(instants, now);
  const kind = scoped ? "DAILY LOG STREAK" : "WALK STREAK";
  if (streak.days === 0) {
    return {
      ...streak,
      kind,
      title: scoped ? "Start a streak — file a daily log" : "Start a streak — walk today",
      sub: scoped ? "The next daily log here starts the count" : "Your next walk starts the count",
    };
  }
  const unit = streak.days === 1 ? "day" : "days";
  const title = streak.doneToday
    ? `${streak.days} ${unit} · ${scoped ? "today’s log is in" : "walked today"}`
    : `${streak.days} ${unit} · ${scoped ? "file today’s log" : "walk today to keep it"}`;
  let sub = "";
  if (streak.last) {
    const when = `${relativeDay(streak.last, now)} ${clock(streak.last)}`;
    if (scoped) sub = `Last filed ${when}`;
    else {
      const lastWalk = walks
        .slice()
        .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())[0];
      const where = lastWalk ? input.siteName(lastWalk.jobSiteId) : "";
      sub = `Last walk ${when}${where ? ` · ${where}` : ""}`;
    }
  }
  return { ...streak, kind, title, sub };
}

// ---------------------------------------------------------------------------
// Records to review / Overdue
// ---------------------------------------------------------------------------

const KIND_WORD: Record<string, [string, string]> = {
  log: ["daily log", "daily logs"],
  observation: ["observation", "observations"],
  task: ["task", "tasks"],
  punch: ["punch item", "punch items"],
};

/**
 * The nav's Review badge counts what is waiting AND what was saved for later
 * (`pendingReviewCount + savedForLaterCount`); this card counts the same, so
 * the two numbers on one screen agree.
 */
export function reviewWidget(rows: ConsoleRow[], pendingCount: number | null, savedForLaterCount: number | null = null) {
  const pending = rows.filter((row) => row.reviewStatus === "pending");
  const saved = savedForLaterCount ?? rows.filter((row) => row.reviewStatus === "deferred").length;
  const waiting = pendingCount ?? pending.length;
  const count = waiting + saved;
  if (count === 0) {
    return { count, clear: true, sub: "All caught up — nothing to review" };
  }
  const byKind = new Map<string, number>();
  for (const row of pending) {
    const kind = isObservation(row) ? "observation" : row.kind;
    byKind.set(kind, (byKind.get(kind) ?? 0) + 1);
  }
  const parts = [...byKind.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([kind, n]) => `${n} ${(KIND_WORD[kind] ?? [kind, kind])[n === 1 ? 0 : 1]}`);
  if (saved > 0) parts.push(`${saved} saved for later`);
  return { count, clear: false, sub: parts.length ? parts.join(", ") : "Waiting on your review" };
}

export function overdueRows(rows: ConsoleRow[], now: Date): ConsoleRow[] {
  const today = startOfDay(now).getTime();
  return rows.filter(
    (row) => isOpen(row) && row.dueDate && new Date(row.dueDate).getTime() < today,
  );
}

export function overdueWidget(rows: ConsoleRow[], now: Date) {
  const late = overdueRows(rows, now);
  if (late.length === 0) return { count: 0, clear: true, sub: "Nothing overdue — nice work" };
  const oldest = Math.max(...late.map((row) => calendarDaysBetween(new Date(row.dueDate!), now)));
  return {
    count: late.length,
    clear: false,
    sub: `Oldest ${oldest} ${oldest === 1 ? "day" : "days"} past due`,
  };
}

// ---------------------------------------------------------------------------
// Punch
// ---------------------------------------------------------------------------

export interface PunchBar {
  name: string;
  /** The number printed at the bar's end. */
  n: number;
  /** Widths, as fractions of the track (0..1). */
  a: number;
  b: number;
}

export interface PunchWidget {
  clear: boolean;
  title: string;
  sub: string;
  legA: string;
  legB: string;
  /** Second legend swatch — red for overdue (one project), quiet for closed. */
  legBRed: boolean;
  bars: PunchBar[];
}

/** Who an item is with: its resolved company, or its resolved person. */
export function holderOf(row: ConsoleRow): string | null {
  return row.assigneeCompany?.trim() || row.assigneeName?.trim() || null;
}

function shortName(name: string): string {
  return name.split(/\s+/)[0] ?? name;
}

export function punchWidget(input: {
  rows: ConsoleRow[];
  scoped: boolean;
  /** Projects newest-walked first — the all-projects view's five. */
  recentSiteIds: string[];
  siteName: (id: string) => string;
  now: Date;
  /** "on this site" vs "across your projects", for the clear state. */
}): PunchWidget {
  const { rows, scoped, now } = input;
  const punch = rows.filter((row) => row.kind === "punch" && row.reviewStatus !== "rejected");
  const open = punch.filter(isOpen);
  const weekAgo = now.getTime() - 7 * DAY_MS;
  const openedThisWeek = punch.filter((row) => new Date(row.createdAt).getTime() >= weekAgo).length;

  if (open.length === 0) {
    return {
      clear: true,
      title: "Punch list is clear",
      sub: scoped ? "Every item on this site is closed." : "Every punch item across your projects is closed.",
      legA: "",
      legB: "",
      legBRed: false,
      bars: [],
    };
  }

  if (scoped) {
    const late = new Set(overdueRows(open, now).map((row) => row.id));
    const byHolder = new Map<string, { n: number; late: number }>();
    for (const row of open) {
      const holder = holderOf(row);
      if (!holder) continue;
      const entry = byHolder.get(holder) ?? { n: 0, late: 0 };
      entry.n += 1;
      if (late.has(row.id)) entry.late += 1;
      byHolder.set(holder, entry);
    }
    const ranked = [...byHolder.entries()].sort((a, b) => b[1].n - a[1].n || a[0].localeCompare(b[0]));
    const max = ranked[0]?.[1].n ?? 1;
    const top = ranked[0];
    return {
      clear: false,
      title: top
        ? `${shortName(top[0])} is holding ${top[1].n} of ${open.length} open ${open.length === 1 ? "item" : "items"}`
        : `${open.length} open punch ${open.length === 1 ? "item" : "items"}`,
      sub: `+${openedThisWeek} this week${late.size ? ` · ${late.size} overdue` : ""}`,
      legA: "On time",
      legB: "Overdue",
      legBRed: true,
      bars: ranked.slice(0, 5).map(([name, entry]) => ({
        name,
        n: entry.n,
        a: (entry.n - entry.late) / max,
        b: entry.late / max,
      })),
    };
  }

  // All projects: open vs closed for the most recently walked projects that
  // carry any punch at all.
  const perSite = new Map<string, { open: number; closed: number }>();
  for (const row of punch) {
    const entry = perSite.get(row.jobSiteId) ?? { open: 0, closed: 0 };
    if (isOpen(row)) entry.open += 1;
    else entry.closed += 1;
    perSite.set(row.jobSiteId, entry);
  }
  const ordered = [
    ...input.recentSiteIds.filter((id) => perSite.has(id)),
    ...[...perSite.keys()].filter((id) => !input.recentSiteIds.includes(id)),
  ].slice(0, 5);
  const max = Math.max(1, ...ordered.map((id) => perSite.get(id)!.open + perSite.get(id)!.closed));
  const openInRecent = ordered.reduce((sum, id) => sum + perSite.get(id)!.open, 0);
  const closedThisWeek = punch.filter(
    (row) => !isOpen(row) && new Date(row.lastMovementAt).getTime() >= weekAgo,
  ).length;
  return {
    clear: false,
    title:
      ordered.length === 1
        ? `${openInRecent} open on ${input.siteName(ordered[0]!)}`
        : `${openInRecent} open across your ${ordered.length} most recent projects`,
    sub: `+${openedThisWeek} opened, ${closedThisWeek} closed this week`,
    legA: "Open",
    legB: "Closed",
    legBRed: false,
    bars: ordered.map((id) => {
      const entry = perSite.get(id)!;
      return { name: input.siteName(id), n: entry.open, a: entry.open / max, b: entry.closed / max };
    }),
  };
}

// ---------------------------------------------------------------------------
// Observations
// ---------------------------------------------------------------------------

export interface ObservationWidget {
  clear: boolean;
  /** The amber "YESTERDAY" marker — when the newest open one was raised. */
  marker: string | null;
  title: string;
  sub: string;
  n: number;
  who: string;
  /** Share of the open observations `who` is holding (0..1). */
  share: number;
}

export function observationWidget(rows: ConsoleRow[], now: Date): ObservationWidget {
  const all = rows.filter((row) => isObservation(row) && row.reviewStatus !== "rejected");
  const open = all.filter(isOpen);
  if (open.length === 0) {
    return { clear: true, marker: null, title: "No open observations", sub: "Clean site — nothing flagged.", n: 0, who: "", share: 0 };
  }
  const newest = open.reduce((a, b) => (new Date(a.createdAt) > new Date(b.createdAt) ? a : b));
  const byHolder = new Map<string, number>();
  for (const row of open) {
    const holder = holderOf(row);
    if (holder) byHolder.set(holder, (byHolder.get(holder) ?? 0) + 1);
  }
  const top = [...byHolder.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
  const dayAgo = now.getTime() - DAY_MS;
  const fresh = open.filter((row) => new Date(row.createdAt).getTime() >= dayAgo).length;
  return {
    clear: false,
    marker: relativeDay(new Date(newest.createdAt), now).toUpperCase(),
    title: top ? "Waiting on" : "Open observations",
    sub: `${open.length} open of ${all.length}` + (fresh ? ` · ${fresh} new in the last day` : ""),
    n: top ? top[1] : open.length,
    who: top ? top[0] : "open",
    share: top ? top[1] / open.length : 1,
  };
}

// ---------------------------------------------------------------------------
// Tasks (all projects) / Days to close (one project)
// ---------------------------------------------------------------------------

export interface LastWidget {
  /** Tasks clear: the eyebrow is TASKS with a check, and the green box shows. */
  clear: boolean;
  kind: "TASKS" | "PUNCH";
  title: string;
  sub: string;
  n: string;
  who: string;
  /** Bar: ink share, then amber share (0..1). */
  a: number;
  b: number;
}

export function lastWidget(input: { rows: ConsoleRow[]; scoped: boolean; now: Date }): LastWidget {
  const { rows, scoped, now } = input;
  const allTasks = rows.filter((row) => row.kind === "task" && row.reviewStatus !== "rejected");
  const tasks = allTasks.filter(isOpen);
  if (tasks.length === 0) {
    return {
      clear: true,
      kind: "TASKS",
      title: "Nothing due this week",
      sub: scoped ? "No open tasks on this project." : "No open tasks on any project.",
      n: "0",
      who: "",
      a: 0,
      b: 0,
    };
  }
  if (!scoped) {
    const weekEnd = startOfDay(now).getTime() + 7 * DAY_MS;
    const late = overdueRows(tasks, now).length;
    const dueSoon = tasks.filter(
      (row) => row.dueDate && new Date(row.dueDate).getTime() < weekEnd,
    ).length;
    return {
      clear: false,
      kind: "TASKS",
      title: "Open tasks",
      sub: `${tasks.length} open of ${allTasks.length} · ${dueSoon} due this week · ${late} overdue`,
      n: String(tasks.length),
      who: "all projects",
      a: (tasks.length - dueSoon) / tasks.length,
      b: dueSoon / tasks.length,
    };
  }
  // One project: how long punch takes to close here.
  const punch = rows.filter((row) => row.kind === "punch" && row.reviewStatus !== "rejected");
  const monthAgo = now.getTime() - 30 * DAY_MS;
  const closed = punch.filter(
    (row) => !isOpen(row) && new Date(row.lastMovementAt).getTime() >= monthAgo,
  );
  const open = punch.filter(isOpen);
  const ageOf = (row: ConsoleRow, until: Date) =>
    Math.max(0, (until.getTime() - new Date(row.createdAt).getTime()) / DAY_MS);
  const avg = (values: number[]) => values.reduce((a, b) => a + b, 0) / Math.max(1, values.length);
  const byHolder = new Map<string, number>();
  for (const row of open) {
    const holder = holderOf(row);
    if (holder) byHolder.set(holder, (byHolder.get(holder) ?? 0) + 1);
  }
  const top = [...byHolder.entries()].sort((a, b) => b[1] - a[1])[0];
  if (closed.length > 0) {
    const days = avg(closed.map((row) => ageOf(row, new Date(row.lastMovementAt))));
    return {
      clear: false,
      kind: "PUNCH",
      title: "Days to close",
      sub: `${closed.length} closed in the last 30 days`,
      n: days.toFixed(1),
      who: top ? shortName(top[0]) : "",
      a: Math.min(1, days / 14) * 0.86,
      b: Math.min(0.14, open.length ? 0.14 : 0),
    };
  }
  const days = avg(open.map((row) => ageOf(row, now)));
  return {
    clear: false,
    kind: "PUNCH",
    title: open.length ? "Days open" : "Open tasks",
    sub: open.length ? "Average age of open punch" : `${tasks.length} on this project`,
    n: open.length ? days.toFixed(1) : String(tasks.length),
    who: top ? shortName(top[0]) : "",
    a: open.length ? Math.min(1, days / 14) * 0.86 : 1,
    b: open.length ? 0.14 : 0,
  };
}

// ---------------------------------------------------------------------------
// Recent projects
// ---------------------------------------------------------------------------

/** Projects, most recently walked first; never-walked ones last, by name. */
export function recentProjects(cards: ProjectCardRow[], sites: ConsoleSite[]): ProjectCardRow[] {
  const known = new Map(cards.map((card) => [card.id, card]));
  const all: ProjectCardRow[] = [
    ...cards,
    ...sites
      .filter((site) => !known.has(site.id))
      .map((site) => ({ id: site.id, name: site.name, syncSource: site.syncSource, toReview: 0, pastDue: 0 })),
  ];
  return all.sort((a, b) => {
    const at = a.lastWalkAt ? new Date(a.lastWalkAt).getTime() : 0;
    const bt = b.lastWalkAt ? new Date(b.lastWalkAt).getTime() : 0;
    return bt - at || (a.name || a.id).localeCompare(b.name || b.id);
  });
}
